import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface SettingRowControlProps {
  "aria-labelledby": string;
}

export interface SettingRowProps {
  title: string;
  description?: ReactNode;
  /**
   * The control (Switch, Slider...). Pass a function to receive
   * `{ "aria-labelledby" }`, pointing at the title, and spread it onto the control's
   * accessible element (e.g. `inputProps` on a Switch).
   */
  control: ReactNode | ((props: SettingRowControlProps) => ReactNode);
  /** Router path; renders a "Review →" link. */
  reviewTo?: string;
  /** Id for the title element; generated when omitted. */
  id?: string;
}

/** A settings line: title and description on the left, control (and review link) on the right. */
export const SettingRow = ({
  title,
  description,
  control,
  reviewTo,
  id,
}: SettingRowProps) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const titleId = id ?? `setting-${generatedId}`;

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography id={titleId} sx={{ fontSize: 15, fontWeight: 700 }}>
          {title}
        </Typography>
        {description && (
          <Typography
            sx={(theme) => ({
              fontSize: 13,
              color: theme.palette.text.secondary,
              mt: 0.5,
            })}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          justifyContent: "flex-end",
        }}
      >
        {reviewTo && (
          <Box
            component={Link}
            to={reviewTo}
            sx={(theme) => ({
              color: theme.palette.primaryInk,
              fontSize: 13,
              fontWeight: 700,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            })}
          >
            {t("common.review", { defaultValue: "Review" })} →
          </Box>
        )}
        {typeof control === "function"
          ? control({ "aria-labelledby": titleId })
          : control}
      </Box>
    </Box>
  );
};

export default SettingRow;
